import { createElement } from 'react';
import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { App } from 'antd';
import {
  stageMasterService,
  workOrderStageService,
  iirService,
  sciService,
  firService,
  firRequestService,
  irnService,
  workstationService,
  shiftService,
  WorkOrderStageMaster,
  WorkOrderStage,
  WorkOrderStageDimension,
  InitialInspectionRequest,
  InitialInspectionFindingsRow,
  InitialInspectionPartLineRow,
  StageCompletionInspection,
  StageInspectionMeasurement,
  FinalInspectionRequest,
  InspectionReleaseNote,
  getQcContextCompanyId,
  Workstation,
  Shift,
} from '../services/qc-workflow.service';
import { userService } from '../services/user.service';
import { useAuthStore } from '@/store/authStore';
import { isTmmdeNotUsableError, useTmmdeUseOverrideAllowed } from './useTmmdeUseOverride';
import { MFG_EXECUTION_BOARD_QUERY_KEY } from '../services/workOrderExecutionBoard.service';

function mfgShopFloorHapticSuccess(): void {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate(20);
    }
  } catch {
    /* ignore */
  }
}

function mfgShopFloorHapticError(): void {
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
      navigator.vibrate([35, 50, 35]);
    }
  } catch {
    /* ignore */
  }
}

function executionBoardAndStageQueryInvalidation(
  queryClient: ReturnType<typeof useQueryClient>,
  workOrderId: string,
  stageId: string
): void {
  queryClient.invalidateQueries({ queryKey: ['work-order-stages'] });
  queryClient.invalidateQueries({ queryKey: ['work-order-stage', stageId] });
  queryClient.invalidateQueries({ queryKey: ['current-stage', workOrderId] });
  queryClient.invalidateQueries({ queryKey: [MFG_EXECUTION_BOARD_QUERY_KEY] });
  queryClient.invalidateQueries({ queryKey: ['work-orders', workOrderId, 'boq-operations'] });
  queryClient.invalidateQueries({ queryKey: ['work-orders', workOrderId] });
}

// =====================================================
// STAGE MASTER HOOKS
// =====================================================

export const useStageMasters = (includeInactive?: boolean): UseQueryResult<WorkOrderStageMaster[], Error> => {
  return useQuery({
    queryKey: ['stage-masters', includeInactive],
    queryFn: async () => {
      const response = await stageMasterService.getAll(includeInactive);
      return response.data?.data || response.data || [];
    },
  });
};

export const useStageMaster = (id: string, enabled: boolean = true): UseQueryResult<WorkOrderStageMaster, Error> => {
  return useQuery({
    queryKey: ['stage-master', id],
    queryFn: async () => {
      const response = await stageMasterService.getById(id);
      return response.data?.data || response.data;
    },
    enabled: enabled && !!id,
  });
};

export const useStageMastersBySequence = (): UseQueryResult<WorkOrderStageMaster[], Error> => {
  return useQuery({
    queryKey: ['stage-masters', 'sequence'],
    queryFn: async () => {
      const response = await stageMasterService.getBySequence();
      return response.data?.data || response.data || [];
    },
  });
};

export const useCreateStageMaster = (): UseMutationResult<WorkOrderStageMaster, Error, Partial<WorkOrderStageMaster>> => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<WorkOrderStageMaster>) => {
      const response = await stageMasterService.create(data);
      return response.data?.data || response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stage-masters'] });
      message.success('Stage master created successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to create stage master');
    },
  });
};

export const useUpdateStageMaster = (): UseMutationResult<WorkOrderStageMaster, Error, { id: string; data: Partial<WorkOrderStageMaster> }> => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<WorkOrderStageMaster> }) => {
      const response = await stageMasterService.update(id, data);
      return response.data?.data || response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stage-masters'] });
      message.success('Stage master updated successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to update stage master');
    },
  });
};

export const useDeleteStageMaster = (): UseMutationResult<void, Error, string> => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await stageMasterService.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stage-masters'] });
      message.success('Stage master deleted successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to delete stage master');
    },
  });
};

// =====================================================
// WORK ORDER STAGE HOOKS
// =====================================================

export const useWorkOrderStages = (workOrderId: string, lineItemId?: string): UseQueryResult<WorkOrderStage[], Error> => {
  return useQuery({
    queryKey: ['work-order-stages', workOrderId, lineItemId],
    queryFn: async () => {
      const response = await workOrderStageService.getStages(workOrderId, lineItemId);
      return response.data?.data || response.data || [];
    },
    enabled: !!workOrderId,
  });
};

export const useWorkOrderStage = (id: string, enabled: boolean = true): UseQueryResult<WorkOrderStage, Error> => {
  return useQuery({
    queryKey: ['work-order-stage', id],
    queryFn: async () => {
      const response = await workOrderStageService.getById(id);
      return response.data?.data || response.data;
    },
    enabled: enabled && !!id,
  });
};

export const useCurrentStage = (workOrderId: string): UseQueryResult<WorkOrderStage | null, Error> => {
  return useQuery({
    queryKey: ['current-stage', workOrderId],
    queryFn: async () => {
      const response = await workOrderStageService.getCurrentStage(workOrderId);
      return response.data?.data || response.data || null;
    },
    enabled: !!workOrderId,
  });
};

// Manual stage creation (user selects from stage masters)
export const useCreateStage = (): UseMutationResult<
  WorkOrderStage, 
  Error, 
  { 
    workOrderId: string; 
    stageData: {
      stage_master_id: string;
      sequence_number: number;
      work_order_line_item_id?: string;
      // Timeline Planning
      planned_start_date?: string;
      planned_end_date?: string;
      estimated_hours?: number;
      // Resource Assignment
      assigned_to_user_id?: string;
      workstation_id?: string;
      shift_id?: string;
      // Priority & Dependencies
      priority?: string;
      is_critical_path?: boolean;
      wait_for_previous?: boolean;
      inspection_required?: boolean;
      // Notes
      notes?: string;
      description?: string;
    };
  }
> => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ workOrderId, stageData }) => {
      const response = await workOrderStageService.createStage(workOrderId, stageData);
      return response.data?.data || response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['work-order-stages', variables.workOrderId] });
      queryClient.invalidateQueries({ queryKey: ['current-stage', variables.workOrderId] });
      message.success('Stage added successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to add stage');
    },
  });
};

// Automatic stage creation from stage masters (kept for backward compatibility)
export const useCreateStages = (): UseMutationResult<WorkOrderStage[], Error, { workOrderId: string; lineItemIds?: string[] }> => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ workOrderId, lineItemIds }: { workOrderId: string; lineItemIds?: string[] }) => {
      const response = await workOrderStageService.createStages(workOrderId, lineItemIds);
      return response.data?.data || response.data || [];
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['work-order-stages', variables.workOrderId] });
      queryClient.invalidateQueries({ queryKey: ['current-stage', variables.workOrderId] });
      message.success('Stages created successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to create stages');
    },
  });
};

export const useUpdateStage = (): UseMutationResult<
  WorkOrderStage,
  Error,
  {
    id: string;
    workOrderId: string;
    data: any;
  }
> => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await workOrderStageService.updateStage(id, data);
      return response.data?.data || response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['work-order-stages', variables.workOrderId] });
      queryClient.invalidateQueries({ queryKey: ['work-order-stage', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['current-stage', variables.workOrderId] });
      queryClient.invalidateQueries({ queryKey: [MFG_EXECUTION_BOARD_QUERY_KEY] });
      message.success('Stage updated successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to update stage');
    },
  });
};

export const useStartStage = (): UseMutationResult<WorkOrderStage, Error, { id: string; notes?: string }> => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes?: string }) => {
      const response = await workOrderStageService.startStage(id, notes);
      return response.data?.data || response.data;
    },
    onSuccess: (data) => {
      const wid = data.work_order_id;
      const sid = data.id;
      executionBoardAndStageQueryInvalidation(queryClient, wid, sid);
      mfgShopFloorHapticSuccess();
      const key = `mfg-wo-ops-undo-start-${sid}`;
      message.open({
        type: 'success',
        key,
        duration: 5,
        content: createElement(
          'span',
          null,
          'Stage started successfully. ',
          createElement(
            'a',
            {
              href: '#',
              style: { textDecoration: 'underline', cursor: 'pointer' },
              onClick: (e: { preventDefault: () => void }) => {
                e.preventDefault();
                void (async () => {
                  message.destroy(key);
                  try {
                    await workOrderStageService.revertStageToPending(sid);
                    executionBoardAndStageQueryInvalidation(queryClient, wid, sid);
                    mfgShopFloorHapticSuccess();
                    message.success('Reverted to Pending', 3);
                  } catch (err) {
                    mfgShopFloorHapticError();
                    message.error(
                      (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
                        'Could not undo — use Revert to Pending on the card if available'
                    );
                  }
                })();
              },
            },
            'Undo'
          )
        ),
      });
    },
    onError: (error: any) => {
      mfgShopFloorHapticError();
      message.error(error.response?.data?.message || 'Failed to start stage');
    },
  });
};

export const useRevertStageToPending = (): UseMutationResult<
  WorkOrderStage,
  Error,
  { id: string; notes?: string }
> => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes?: string }) => {
      const response = await workOrderStageService.revertStageToPending(id, notes);
      return response.data?.data || response.data;
    },
    onSuccess: (data) => {
      const wid = data.work_order_id;
      queryClient.invalidateQueries({ queryKey: ['work-order-stages'] });
      queryClient.invalidateQueries({ queryKey: ['work-order-stage', data.id] });
      queryClient.invalidateQueries({ queryKey: ['current-stage', wid] });
      queryClient.invalidateQueries({ queryKey: ['work-orders', wid, 'boq-operations'] });
      queryClient.invalidateQueries({ queryKey: [MFG_EXECUTION_BOARD_QUERY_KEY] });
      mfgShopFloorHapticSuccess();
      message.success('Stage moved back to Pending', 5);
    },
    onError: (error: any) => {
      mfgShopFloorHapticError();
      message.error(error.response?.data?.message || 'Failed to revert stage');
    },
  });
};

export const useStartOutsourcedStageWithPr = (): UseMutationResult<
  { stage: WorkOrderStage; purchaseRequisition: { id: string; pr_number?: string } },
  Error,
  {
    id: string;
    body: {
      pr_date: string;
      required_date: string;
      department_id: string;
      notes?: string;
      lines: Array<{
        item_id: string;
        requested_quantity: number;
        uom: string;
        estimated_unit_price?: number;
        specifications?: string;
        notes?: string;
      }>;
    };
  }
> => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, body }) => {
      const response = await workOrderStageService.startOutsourcedWithPr(id, body);
      return response.data?.data || response.data;
    },
    onSuccess: (data) => {
      const wid = data?.stage?.work_order_id;
      queryClient.invalidateQueries({ queryKey: ['work-order-stages'] });
      if (data?.stage?.id) {
        queryClient.invalidateQueries({ queryKey: ['work-order-stage', data.stage.id] });
      }
      if (wid) {
        queryClient.invalidateQueries({ queryKey: ['current-stage', wid] });
        queryClient.invalidateQueries({ queryKey: ['work-orders', wid, 'boq-operations'] });
      }
      queryClient.invalidateQueries({ queryKey: ['mfg-execution-board'] });
      message.success('Purchase Request created; stage is in progress');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to start outsourced stage');
    },
  });
};

export const useRequestInspection = (): UseMutationResult<WorkOrderStage, Error, { id: string; notes?: string }> => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes?: string }) => {
      const response = await workOrderStageService.requestInspection(id, notes);
      return response.data?.data || response.data;
    },
    onSuccess: (data) => {
      const wid = data.work_order_id;
      const sid = data.id;
      executionBoardAndStageQueryInvalidation(queryClient, wid, sid);
      queryClient.invalidateQueries({ queryKey: ['stage-inspections'] });
      queryClient.invalidateQueries({ queryKey: ['stage-inspection', 'stage', sid] });
      mfgShopFloorHapticSuccess();
      message.success('Inspection requested – stage paused for quality review');
    },
    onError: (error: any) => {
      mfgShopFloorHapticError();
      message.error(error.response?.data?.message || 'Failed to request inspection');
    },
  });
};

export const useCompleteStage = (): UseMutationResult<WorkOrderStage, Error, { id: string; notes?: string }> => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes?: string }) => {
      const response = await workOrderStageService.completeStage(id, notes);
      return response.data?.data || response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['work-order-stages'] });
      queryClient.invalidateQueries({ queryKey: ['work-order-stage', data.id] });
      queryClient.invalidateQueries({ queryKey: ['current-stage', data.work_order_id] });
      queryClient.invalidateQueries({ queryKey: ['stage-inspections'] });
      queryClient.invalidateQueries({ queryKey: [MFG_EXECUTION_BOARD_QUERY_KEY] });
      mfgShopFloorHapticSuccess();
      message.open({
        type: 'success',
        duration: 5,
        content: createElement(
          'div',
          null,
          createElement('div', { style: { fontWeight: 600 } }, 'Stage completed successfully'),
          createElement(
            'div',
            {
              style: {
                fontSize: 12,
                marginTop: 8,
                lineHeight: 1.5,
                maxWidth: 420,
                color: 'rgba(0,0,0,0.75)',
              },
            },
            'There is no in-app Undo after Complete (no API). If this was a mistake, follow your work order process or ask a supervisor to correct the operation record.'
          )
        ),
      });
    },
    onError: (error: any) => {
      mfgShopFloorHapticError();
      message.error(error.response?.data?.message || 'Failed to complete stage');
    },
  });
};

export const usePauseStage = (): UseMutationResult<WorkOrderStage, Error, { id: string; notes?: string }> => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes?: string }) => {
      const response = await workOrderStageService.pauseStage(id, notes);
      return response.data?.data || response.data;
    },
    onSuccess: (data) => {
      const wid = data.work_order_id;
      const sid = data.id;
      executionBoardAndStageQueryInvalidation(queryClient, wid, sid);
      mfgShopFloorHapticSuccess();
      const key = `mfg-wo-ops-undo-pause-${sid}`;
      message.open({
        type: 'success',
        key,
        duration: 5,
        content: createElement(
          'span',
          null,
          'Stage paused. ',
          createElement(
            'a',
            {
              href: '#',
              style: { textDecoration: 'underline', cursor: 'pointer' },
              onClick: (e: { preventDefault: () => void }) => {
                e.preventDefault();
                void (async () => {
                  message.destroy(key);
                  try {
                    await workOrderStageService.resumeStage(sid);
                    executionBoardAndStageQueryInvalidation(queryClient, wid, sid);
                    mfgShopFloorHapticSuccess();
                    message.success('Resumed', 3);
                  } catch (err) {
                    mfgShopFloorHapticError();
                    message.error(
                      (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
                        'Could not resume from undo'
                    );
                  }
                })();
              },
            },
            'Undo'
          )
        ),
      });
    },
    onError: (error: any) => {
      mfgShopFloorHapticError();
      message.error(error.response?.data?.message || 'Failed to pause stage');
    },
  });
};

export const useHoldStage = (): UseMutationResult<WorkOrderStage, Error, { id: string; notes?: string }> => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes?: string }) => {
      const response = await workOrderStageService.holdStage(id, notes);
      return response.data?.data || response.data;
    },
    onSuccess: (data) => {
      const wid = data.work_order_id;
      const sid = data.id;
      executionBoardAndStageQueryInvalidation(queryClient, wid, sid);
      mfgShopFloorHapticSuccess();
      const key = `mfg-wo-ops-undo-hold-${sid}`;
      message.open({
        type: 'success',
        key,
        duration: 5,
        content: createElement(
          'span',
          null,
          'Stage on hold. ',
          createElement(
            'a',
            {
              href: '#',
              style: { textDecoration: 'underline', cursor: 'pointer' },
              onClick: (e: { preventDefault: () => void }) => {
                e.preventDefault();
                void (async () => {
                  message.destroy(key);
                  try {
                    await workOrderStageService.resumeStage(sid);
                    executionBoardAndStageQueryInvalidation(queryClient, wid, sid);
                    mfgShopFloorHapticSuccess();
                    message.success('Resumed', 3);
                  } catch (err) {
                    mfgShopFloorHapticError();
                    message.error(
                      (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
                        'Could not resume from undo'
                    );
                  }
                })();
              },
            },
            'Undo'
          )
        ),
      });
    },
    onError: (error: any) => {
      mfgShopFloorHapticError();
      message.error(error.response?.data?.message || 'Failed to hold stage');
    },
  });
};

export const useResumeStage = (): UseMutationResult<WorkOrderStage, Error, { id: string; notes?: string }> => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes?: string }) => {
      const response = await workOrderStageService.resumeStage(id, notes);
      return response.data?.data || response.data;
    },
    onSuccess: (data) => {
      const wid = data.work_order_id;
      const sid = data.id;
      executionBoardAndStageQueryInvalidation(queryClient, wid, sid);
      mfgShopFloorHapticSuccess();
      const key = `mfg-wo-ops-undo-resume-${sid}`;
      message.open({
        type: 'success',
        key,
        duration: 5,
        content: createElement(
          'span',
          null,
          'Stage resumed. ',
          createElement(
            'a',
            {
              href: '#',
              style: { textDecoration: 'underline', cursor: 'pointer' },
              onClick: (e: { preventDefault: () => void }) => {
                e.preventDefault();
                void (async () => {
                  message.destroy(key);
                  try {
                    await workOrderStageService.pauseStage(sid);
                    executionBoardAndStageQueryInvalidation(queryClient, wid, sid);
                    mfgShopFloorHapticSuccess();
                    message.success('Paused again', 3);
                  } catch (err) {
                    mfgShopFloorHapticError();
                    message.error(
                      (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
                        'Could not reverse resume'
                    );
                  }
                })();
              },
            },
            'Undo'
          )
        ),
      });
    },
    onError: (error: any) => {
      mfgShopFloorHapticError();
      message.error(error.response?.data?.message || 'Failed to resume stage');
    },
  });
};

export const useStageDimensions = (stageId: string): UseQueryResult<WorkOrderStageDimension[], Error> => {
  return useQuery({
    queryKey: ['stage-dimensions', stageId],
    queryFn: async () => {
      const response = await workOrderStageService.getDimensions(stageId);
      return response.data?.data || response.data || [];
    },
    enabled: !!stageId,
  });
};

export const useAddStageDimension = (): UseMutationResult<WorkOrderStageDimension, Error, { stageId: string; data: Partial<WorkOrderStageDimension> }> => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ stageId, data }: { stageId: string; data: Partial<WorkOrderStageDimension> }) => {
      const response = await workOrderStageService.addDimension(stageId, data);
      return response.data?.data || response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['stage-dimensions', variables.stageId] });
      message.success('Dimension added successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to add dimension');
    },
  });
};

// =====================================================
// INITIAL INSPECTION REQUEST (IIR) HOOKS
// =====================================================

export const useIIRs = (filters?: { status?: string; workOrderId?: string }): UseQueryResult<InitialInspectionRequest[], Error> => {
  return useQuery({
    queryKey: ['iirs', filters],
    queryFn: async () => {
      const response = await iirService.getAll(filters);
      return response.data?.data || response.data || [];
    },
  });
};

export const useIIR = (id: string, enabled: boolean = true): UseQueryResult<InitialInspectionRequest, Error> => {
  return useQuery({
    queryKey: ['iir', id],
    queryFn: async () => {
      const response = await iirService.getById(id);
      return response.data?.data || response.data;
    },
    enabled: enabled && !!id,
  });
};

export const useIIRByWorkOrder = (workOrderId: string): UseQueryResult<InitialInspectionRequest | null, Error> => {
  return useQuery({
    queryKey: ['iir', 'work-order', workOrderId],
    queryFn: async () => {
      const response = await iirService.getByWorkOrder(workOrderId);
      return response.data?.data || response.data || null;
    },
    enabled: !!workOrderId,
    staleTime: 30000, // Consider data fresh for 30 seconds
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    retry: 1, // Only retry once on failure
  });
};

export const useCreateIIR = (): UseMutationResult<InitialInspectionRequest, Error, string> => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (workOrderId: string) => {
      const response = await iirService.createForWorkOrder(workOrderId);
      return response.data?.data || response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['iirs'] });
      queryClient.invalidateQueries({ queryKey: ['iir', 'work-order', data.work_order_id] });
      queryClient.invalidateQueries({ queryKey: ['work-orders', data.work_order_id] });
      message.success('Job Inspection created successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to create IIR');
    },
  });
};

export const useAssignInspector = (): UseMutationResult<InitialInspectionRequest, Error, { id: string; inspectorId: string }> => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, inspectorId }: { id: string; inspectorId: string }) => {
      const response = await iirService.assignInspector(id, inspectorId);
      return response.data?.data || response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['iirs'] });
      queryClient.invalidateQueries({ queryKey: ['iir', data.id] });
      queryClient.invalidateQueries({ queryKey: ['iir', 'work-order', data.work_order_id] });
      queryClient.invalidateQueries({ queryKey: ['work-order-blocked', data.work_order_id] });
      message.success('Inspector assigned successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to assign inspector');
    },
  });
};

export const useUpdateIIRInspectionDetails = (): UseMutationResult<
  InitialInspectionRequest,
  Error,
  {
    id: string;
    data: {
      inspection_date?: string;
      findings?: string;
      observations?: string;
      supporting_documents?: string[];
      photos?: string[];
      report_date?: string | null;
      report_title?: string | null;
      report_subtitle?: string | null;
      report_no?: string | null;
      job_no?: string | null;
      wr_no?: string | null;
      client_ref_1?: string | null;
      client_ref_2?: string | null;
      po_no?: string | null;
      make?: string | null;
      gate_pass_no?: string | null;
      qty_no?: string | null;
      customer_name?: string | null;
      work_description?: string | null;
      equipment_name?: string | null;
      scope_of_work_text?: string | null;
      remarks?: string | null;
      findings_rows?: InitialInspectionFindingsRow[] | null;
      parts_replace_rows?: InitialInspectionPartLineRow[] | null;
      parts_repair_rows?: InitialInspectionPartLineRow[] | null;
      prepared_by_name?: string | null;
      prepared_by_print_name?: string | null;
      prepared_by_designation?: string | null;
      approved_by_name?: string | null;
      approved_by_print_name?: string | null;
      approved_by_designation?: string | null;
    };
  }
> => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await iirService.updateInspectionDetails(id, data);
      return response.data?.data || response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['iirs'] });
      queryClient.invalidateQueries({ queryKey: ['iir', data.id] });
      queryClient.invalidateQueries({ queryKey: ['iir', 'work-order', data.work_order_id] });
      message.success('Initial inspection report details updated');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to update inspection details');
    },
  });
};

export const useApproveIIR = (): UseMutationResult<InitialInspectionRequest, Error, string> => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await iirService.approve(id);
      return response.data?.data || response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['iirs'] });
      queryClient.invalidateQueries({ queryKey: ['iir', data.id] });
      queryClient.invalidateQueries({ queryKey: ['iir', 'work-order', data.work_order_id] });
      queryClient.invalidateQueries({ queryKey: ['work-orders', data.work_order_id] });
      message.success('Job Inspection approved successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to approve IIR');
    },
  });
};

export const useRejectIIR = (): UseMutationResult<InitialInspectionRequest, Error, { id: string; rejectionReason: string }> => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, rejectionReason }: { id: string; rejectionReason: string }) => {
      const response = await iirService.reject(id, rejectionReason);
      return response.data?.data || response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['iirs'] });
      queryClient.invalidateQueries({ queryKey: ['iir', data.id] });
      queryClient.invalidateQueries({ queryKey: ['iir', 'work-order', data.work_order_id] });
      message.warning('Job Inspection rejected');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to reject IIR');
    },
  });
};

export const useCheckWorkOrderBlocked = (
  workOrderId: string,
  options?: { enabled?: boolean }
): UseQueryResult<{ isBlocked: boolean }, Error> => {
  return useQuery({
    queryKey: ['work-order-blocked', workOrderId],
    queryFn: async () => {
      const response = await iirService.checkWorkOrderBlocked(workOrderId);
      return response.data?.data || response.data;
    },
    enabled: options?.enabled !== undefined ? options.enabled : !!workOrderId,
    staleTime: 10000, // 10 seconds
    gcTime: 60 * 1000, // 1 minute cache
  });
};

// =====================================================
// STAGE COMPLETION INSPECTION (SCI) HOOKS
// =====================================================

export const useStageInspectionsAll = (filters?: { work_order_id?: string; status?: string }): UseQueryResult<StageCompletionInspection[], Error> => {
  return useQuery({
    queryKey: ['stage-inspections', 'all', filters],
    queryFn: async () => {
      const response = await sciService.getAll(filters);
      return response.data?.data || response.data || [];
    },
  });
};

export const useStageInspections = (workOrderId: string): UseQueryResult<StageCompletionInspection[], Error> => {
  return useQuery({
    queryKey: ['stage-inspections', workOrderId],
    queryFn: async () => {
      const response = await sciService.getByWorkOrder(workOrderId);
      return response.data?.data || response.data || [];
    },
    enabled: !!workOrderId,
  });
};

export const useStageInspection = (id: string, enabled: boolean = true): UseQueryResult<StageCompletionInspection, Error> => {
  return useQuery({
    queryKey: ['stage-inspection', id],
    queryFn: async () => {
      const response = await sciService.getById(id);
      return response.data?.data || response.data;
    },
    enabled: enabled && !!id,
  });
};

export const useStageInspectionByStage = (stageId: string): UseQueryResult<StageCompletionInspection | null, Error> => {
  return useQuery({
    queryKey: ['stage-inspection', 'stage', stageId],
    queryFn: async () => {
      const response = await sciService.getByStage(stageId);
      return response.data?.data || response.data || null;
    },
    enabled: !!stageId,
  });
};

export interface InspectionStageSummary {
  inspection_id: string;
  inspection_number: string;
  result: string;
  status: string;
  rejection_reason: string | null;
  findings: string | null;
  observations: string | null;
  inspector_name: string | null;
  inspection_date: string | null;
  failed_checkpoints: Array<{ type: string; description: string | null; remarks: string | null }>;
  attachment_count: number;
}

export const useLatestStageInspectionSummary = (
  stageId: string,
  enabled: boolean = true
): UseQueryResult<InspectionStageSummary | null, Error> => {
  return useQuery({
    queryKey: ['stage-inspection-summary', stageId],
    queryFn: async () => {
      const response = await sciService.getLatestSummaryByStage(stageId);
      return response.data?.data || response.data || null;
    },
    enabled: enabled && !!stageId,
  });
};

export const useCreateStageInspection = (): UseMutationResult<StageCompletionInspection, Error, string> => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (stageId: string) => {
      const response = await sciService.createForStage(stageId);
      return response.data?.data || response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['stage-inspections'] });
      queryClient.invalidateQueries({ queryKey: ['stage-inspection', 'stage', data.work_order_stage_id] });
      message.success('Stage Completion Inspection created successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to create stage inspection');
    },
  });
};

export const useStartInspection = (): UseMutationResult<StageCompletionInspection, Error, string> => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (inspectionId: string) => {
      const response = await sciService.startInspection(inspectionId);
      return response.data?.data || response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['stage-inspections'] });
      queryClient.invalidateQueries({ queryKey: ['stage-inspection', data.id] });
      queryClient.invalidateQueries({ queryKey: ['stage-inspection', 'stage', data.work_order_stage_id] });
      message.success('Inspection started successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to start inspection');
    },
  });
};

export const useRecordMeasurements = (): UseMutationResult<StageInspectionMeasurement[], Error, { id: string; measurements: Array<any> }> => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, measurements }: { id: string; measurements: Array<any> }) => {
      const response = await sciService.recordMeasurements(id, measurements);
      return response.data?.data || response.data || [];
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['stage-inspection', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['stage-inspection-measurements', variables.id] });
      message.success('Measurements recorded successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to record measurements');
    },
  });
};

export const useCompleteStageInspection = (): UseMutationResult<StageCompletionInspection, Error, { id: string; data: any }> => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      const response = await sciService.completeInspection(id, data);
      return response.data?.data || response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['stage-inspections'] });
      queryClient.invalidateQueries({ queryKey: ['stage-inspection', data.id] });
      message.success(
        data.status === 'Pending Approval'
          ? 'Stage inspection submitted for approval'
          : 'Stage inspection completed successfully'
      );
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to complete stage inspection');
    },
  });
};

export const useStageInspectionMeasurements = (inspectionId: string): UseQueryResult<StageInspectionMeasurement[], Error> => {
  return useQuery({
    queryKey: ['stage-inspection-measurements', inspectionId],
    queryFn: async () => {
      const response = await sciService.getMeasurements(inspectionId);
      return response.data?.data || response.data || [];
    },
    enabled: !!inspectionId,
  });
};

type UpdateReportMetadataVars = {
  id: string;
  report_metadata: Record<string, unknown>;
  tmmde_override_reason?: string;
};

export const useUpdateReportMetadata = (): UseMutationResult<StageCompletionInspection, Error, UpdateReportMetadataVars> => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const canOverrideEquipment = useTmmdeUseOverrideAllowed();

  return useMutation({
    mutationFn: async ({ id, report_metadata, tmmde_override_reason }: UpdateReportMetadataVars) => {
      const response = await sciService.updateReportMetadata(id, report_metadata, tmmde_override_reason);
      return response.data?.data || response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['stage-inspection', data.id] });
      queryClient.invalidateQueries({ queryKey: ['stage-inspection', 'stage', data.work_order_stage_id] });
      message.success('Report information saved');
    },
    onError: (error: any) => {
      if (canOverrideEquipment && isTmmdeNotUsableError(error)) return;
      message.error(error.response?.data?.message || 'Failed to save report information');
    },
  });
};

// =====================================================
// INSPECTION CHECKPOINT HOOKS
// =====================================================

export const useInspectionCheckpoints = (inspectionId: string): UseQueryResult<any[], Error> => {
  return useQuery({
    queryKey: ['inspection-checkpoints', inspectionId],
    queryFn: async () => {
      const response = await sciService.getCheckpoints(inspectionId);
      return response.data?.data || response.data || [];
    },
    enabled: !!inspectionId,
  });
};

export const useAddCheckpoint = (): UseMutationResult<any, Error, { inspectionId: string; data: any }> => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ inspectionId, data }: { inspectionId: string; data: any }) => {
      const response = await sciService.addCheckpoint(inspectionId, data);
      return response.data?.data || response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['inspection-checkpoints', variables.inspectionId] });
      message.success('Checkpoint added');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to add checkpoint');
    },
  });
};

export const useUpdateCheckpoint = (): UseMutationResult<any, Error, { checkpointId: string; inspectionId: string; data: any }> => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ checkpointId, data }: { checkpointId: string; inspectionId: string; data: any }) => {
      const response = await sciService.updateCheckpoint(checkpointId, data);
      return response.data?.data || response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['inspection-checkpoints', variables.inspectionId] });
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to update checkpoint');
    },
  });
};

export const useDeleteCheckpoint = (): UseMutationResult<void, Error, { checkpointId: string; inspectionId: string }> => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ checkpointId }: { checkpointId: string; inspectionId: string }) => {
      await sciService.deleteCheckpoint(checkpointId);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['inspection-checkpoints', variables.inspectionId] });
      message.success('Checkpoint removed');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to remove checkpoint');
    },
  });
};

// =====================================================
// INSPECTION ATTACHMENT HOOKS
// =====================================================

export const useInspectionAttachments = (inspectionId: string): UseQueryResult<any[], Error> => {
  return useQuery({
    queryKey: ['inspection-attachments', inspectionId],
    queryFn: async () => {
      const response = await sciService.getAttachments(inspectionId);
      return response.data?.data || response.data || [];
    },
    enabled: !!inspectionId,
  });
};

export const useUploadInspectionAttachment = (): UseMutationResult<any, Error, { inspectionId: string; file: File; category?: string; remarks?: string }> => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ inspectionId, file, category, remarks }: { inspectionId: string; file: File; category?: string; remarks?: string }) => {
      const response = await sciService.uploadAttachment(inspectionId, file, category, remarks);
      return response.data?.data || response.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['inspection-attachments', variables.inspectionId] });
      message.success('Attachment uploaded');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to upload attachment');
    },
  });
};

export const useDeleteInspectionAttachment = (): UseMutationResult<void, Error, { attachmentId: string; inspectionId: string }> => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ attachmentId }: { attachmentId: string; inspectionId: string }) => {
      await sciService.deleteAttachment(attachmentId);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['inspection-attachments', variables.inspectionId] });
      message.success('Attachment removed');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to remove attachment');
    },
  });
};

// =====================================================
// INSPECTION HOLD-POINT & WITNESS HOOKS
// =====================================================

export const useReleaseInspectionHoldPoint = (): UseMutationResult<StageCompletionInspection, Error, { inspectionId: string; notes?: string }> => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ inspectionId, notes }: { inspectionId: string; notes?: string }) => {
      const response = await sciService.releaseHoldPoint(inspectionId, { notes });
      return response.data?.data || response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['stage-inspection', data.id] });
      queryClient.invalidateQueries({ queryKey: ['stage-inspections'] });
      message.success('Hold point released');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to release hold point');
    },
  });
};

export const useCompleteInspectionWitness = (): UseMutationResult<StageCompletionInspection, Error, { inspectionId: string; data: { witness_name: string; witness_party: string; witness_signature?: any } }> => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ inspectionId, data }: { inspectionId: string; data: { witness_name: string; witness_party: string; witness_signature?: any } }) => {
      const response = await sciService.completeWitnessPoint(inspectionId, data);
      return response.data?.data || response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['stage-inspection', data.id] });
      queryClient.invalidateQueries({ queryKey: ['stage-inspections'] });
      message.success('Witness sign-off completed');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to complete witness sign-off');
    },
  });
};

// =====================================================
// FINAL INSPECTION REQUEST (FIR) HOOKS
// =====================================================

export const useFIRs = (filters?: { status?: string; workOrderId?: string }): UseQueryResult<FinalInspectionRequest[], Error> => {
  return useQuery({
    queryKey: ['firs', filters],
    queryFn: async () => {
      const response = await firService.getAll(filters);
      return response.data?.data || response.data || [];
    },
  });
};

export const useFIR = (id: string, enabled: boolean = true): UseQueryResult<FinalInspectionRequest, Error> => {
  return useQuery({
    queryKey: ['fir', id],
    queryFn: async () => {
      const response = await firService.getById(id);
      return response.data?.data || response.data;
    },
    enabled: enabled && !!id,
  });
};

/** Prefer an Approved report when multiple FIR rows exist for one WO (newest first within each group). */
function pickPrimaryFirForWorkOrder(
  list: FinalInspectionRequest[]
): FinalInspectionRequest | null {
  if (list.length === 0) return null;
  if (list.length === 1) return list[0];
  const ts = (r: FinalInspectionRequest) =>
    new Date(r.updated_at || r.created_at).getTime();
  const approved = list.filter(
    (r) => r.status === 'Approved' || (r as { approval_status?: string }).approval_status === 'Approved'
  );
  const pool = approved.length > 0 ? approved : list;
  return [...pool].sort((a, b) => ts(b) - ts(a))[0];
}

export const useFIRsByWorkOrder = (workOrderId: string): UseQueryResult<FinalInspectionRequest[], Error> => {
  return useQuery({
    queryKey: ['firs', 'work-order', workOrderId],
    queryFn: async () => {
      const response = await firRequestService.getByWorkOrder(workOrderId);
      const raw = response.data?.data ?? response.data;
      return Array.isArray(raw) ? (raw as FinalInspectionRequest[]) : raw ? [raw as FinalInspectionRequest] : [];
    },
    enabled: !!workOrderId,
  });
};

export const useFIRByWorkOrder = (workOrderId: string): UseQueryResult<FinalInspectionRequest | null, Error> => {
  return useQuery({
    queryKey: ['fir', 'work-order', workOrderId],
    queryFn: async () => {
      const response = await firRequestService.getByWorkOrder(workOrderId);
      const raw = response.data?.data ?? response.data;
      const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
      return pickPrimaryFirForWorkOrder(list as FinalInspectionRequest[]);
    },
    enabled: !!workOrderId,
  });
};

export const useCreateFIR = (): UseMutationResult<FinalInspectionRequest, Error, string> => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (workOrderId: string) => {
      const response = await firRequestService.createForWorkOrder(workOrderId);
      return response.data?.data || response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['firs'] });
      queryClient.invalidateQueries({ queryKey: ['fir', 'work-order', data.work_order_id] });
      queryClient.invalidateQueries({ queryKey: ['firs', 'work-order', data.work_order_id] });
      queryClient.invalidateQueries({ queryKey: ['work-orders', data.work_order_id] });
      message.success('Final Inspection Request created successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to create FIR');
    },
  });
};

export const useApproveFIR = (): UseMutationResult<FinalInspectionRequest, Error, string> => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await firRequestService.approve(id);
      return response.data?.data || response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['firs'] });
      queryClient.invalidateQueries({ queryKey: ['fir', data.id] });
      queryClient.invalidateQueries({ queryKey: ['fir', 'work-order', data.work_order_id] });
      queryClient.invalidateQueries({ queryKey: ['firs', 'work-order', data.work_order_id] });
      queryClient.invalidateQueries({ queryKey: ['work-orders', data.work_order_id] });
      message.success('FIR approved successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to approve FIR');
    },
  });
};

export const useRejectFIR = (): UseMutationResult<any, Error, { id: string; rejectionReason: string }> => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, rejectionReason }: { id: string; rejectionReason: string }) => {
      const response = await firRequestService.reject(id, rejectionReason);
      return response.data?.data || response.data;
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['firs'] });
      const firRow = data?.fir ?? data;
      if (firRow?.id) queryClient.invalidateQueries({ queryKey: ['fir', firRow.id] });
      if (firRow?.work_order_id) {
        queryClient.invalidateQueries({ queryKey: ['fir', 'work-order', firRow.work_order_id] });
        queryClient.invalidateQueries({ queryKey: ['firs', 'work-order', firRow.work_order_id] });
      }
      queryClient.invalidateQueries({ queryKey: ['work-orders'] });
      queryClient.invalidateQueries({ queryKey: ['ncrs'] });
      message.warning('FIR rejected. NCR and revised work orders created');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to reject FIR');
    },
  });
};

export const useDeleteFIR = (): UseMutationResult<void, Error, { id: string; workOrderId: string }> => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id }: { id: string; workOrderId: string }) => {
      await firService.delete(id);
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['fir', 'work-order', variables.workOrderId] });
      queryClient.invalidateQueries({ queryKey: ['firs', 'work-order', variables.workOrderId] });
      queryClient.invalidateQueries({ queryKey: ['final-inspection-reports'] });
      queryClient.invalidateQueries({ queryKey: ['work-orders'] });
      message.success('Final Inspection Report deleted. You can create a new one.');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to delete FIR');
    },
  });
};

// =====================================================
// INSPECTION RELEASE NOTE (IRN) HOOKS
// =====================================================

export const useIRNs = (
  filters?: { status?: string; workOrderId?: string; deliveryNoteId?: string }
): UseQueryResult<InspectionReleaseNote[], Error> => {
  return useQuery({
    queryKey: ['irns', filters],
    queryFn: async () => {
      const response = await irnService.getAll(filters);
      return response.data?.data || response.data || [];
    },
  });
};

export const useIRN = (id: string, enabled: boolean = true): UseQueryResult<InspectionReleaseNote, Error> => {
  return useQuery({
    queryKey: ['irn', id],
    queryFn: async () => {
      const response = await irnService.getById(id);
      return response.data?.data || response.data;
    },
    enabled: enabled && !!id,
  });
};

// Final Inspection Request by work order (used by IRN – must be approved before creating IRN)
export const useFIRRequestByWorkOrder = (workOrderId: string): UseQueryResult<FinalInspectionRequest | null, Error> => {
  return useQuery({
    queryKey: ['fir-request', 'work-order', workOrderId],
    queryFn: async () => {
      const response = await firRequestService.getByWorkOrder(workOrderId);
      const raw = response.data?.data ?? response.data;
      const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
      return pickPrimaryFirForWorkOrder(list as FinalInspectionRequest[]);
    },
    enabled: !!workOrderId,
  });
};

export const useSyncFIRFromReport = (workOrderId: string): UseMutationResult<{ synced: boolean; reason?: string }, Error, void> => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await firRequestService.syncFromReport(workOrderId);
      const data = response.data?.data ?? response.data;
      return data ?? { synced: false };
    },
    onSuccess: async (data) => {
      queryClient.invalidateQueries({ queryKey: ['fir-request', 'work-order', workOrderId] });
      queryClient.invalidateQueries({ queryKey: ['irn', 'work-order', workOrderId] });
      if (data?.synced) {
        await queryClient.refetchQueries({ queryKey: ['fir-request', 'work-order', workOrderId] });
        message.success('FIR Request synced from Report.');
      } else {
        message.info(data?.reason ?? 'No sync performed');
      }
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Sync from report failed');
    },
  });
};

export const useIRNByWorkOrder = (workOrderId: string): UseQueryResult<InspectionReleaseNote | null, Error> => {
  return useQuery({
    queryKey: ['irn', 'work-order', workOrderId],
    queryFn: async () => {
      const response = await irnService.getByWorkOrder(workOrderId);
      return response.data?.data || response.data || null;
    },
    enabled: !!workOrderId,
  });
};

export const useIRNByDeliveryNote = (deliveryNoteId: string): UseQueryResult<InspectionReleaseNote | null, Error> => {
  return useQuery({
    queryKey: ['irn', 'delivery-note', deliveryNoteId],
    queryFn: async () => {
      const response = await irnService.getByDeliveryNote(deliveryNoteId);
      return response.data?.data || response.data || null;
    },
    enabled: !!deliveryNoteId,
  });
};

export const useCreateIRNForDeliveryNote = (): UseMutationResult<InspectionReleaseNote, Error, string> => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (deliveryNoteId: string) => {
      const response = await irnService.createForDeliveryNote(deliveryNoteId);
      return response.data?.data || response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['irns'] });
      if (data?.delivery_note_id) {
        queryClient.invalidateQueries({ queryKey: ['irn', 'delivery-note', data.delivery_note_id] });
      }
      if (data?.work_order_id) {
        queryClient.invalidateQueries({ queryKey: ['irn', 'work-order', data.work_order_id] });
        queryClient.invalidateQueries({ queryKey: ['work-orders', data.work_order_id] });
      }
      message.success('Inspection Release Note created successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to create IRN');
    },
  });
};

export const useCreateIRN = (): UseMutationResult<InspectionReleaseNote, Error, string> => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (firId: string) => {
      const response = await irnService.createForFIR(firId);
      return response.data?.data || response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['irns'] });
      if (data?.work_order_id) {
        queryClient.invalidateQueries({ queryKey: ['irn', 'work-order', data.work_order_id] });
        queryClient.invalidateQueries({ queryKey: ['work-orders', data.work_order_id] });
      }
      if (data?.delivery_note_id) {
        queryClient.invalidateQueries({ queryKey: ['irn', 'delivery-note', data.delivery_note_id] });
      }
      message.success('Inspection Release Note created successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to create IRN');
    },
  });
};

export const useApproveIRN = (): UseMutationResult<InspectionReleaseNote, Error, { id: string; notes?: string }> => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes?: string }) => {
      const response = await irnService.approve(id, notes);
      return response.data?.data || response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['irns'] });
      queryClient.invalidateQueries({ queryKey: ['irn', data.id] });
      if (data?.work_order_id) {
        queryClient.invalidateQueries({ queryKey: ['irn', 'work-order', data.work_order_id] });
        queryClient.invalidateQueries({ queryKey: ['work-orders', data.work_order_id] });
      }
      if (data?.delivery_note_id) {
        queryClient.invalidateQueries({ queryKey: ['irn', 'delivery-note', data.delivery_note_id] });
      }
      message.success('IRN approved successfully');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to approve IRN');
    },
  });
};

export const useRejectIRN = (): UseMutationResult<InspectionReleaseNote, Error, { id: string; rejectionReason: string }> => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, rejectionReason }: { id: string; rejectionReason: string }) => {
      const response = await irnService.reject(id, rejectionReason);
      return response.data?.data || response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['irns'] });
      queryClient.invalidateQueries({ queryKey: ['irn', data.id] });
      if (data?.work_order_id) {
        queryClient.invalidateQueries({ queryKey: ['irn', 'work-order', data.work_order_id] });
        queryClient.invalidateQueries({ queryKey: ['work-orders', data.work_order_id] });
      }
      if (data?.delivery_note_id) {
        queryClient.invalidateQueries({ queryKey: ['irn', 'delivery-note', data.delivery_note_id] });
      }
      message.warning('IRN rejected');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to reject IRN');
    },
  });
};

export const useCancelIRN = (): UseMutationResult<InspectionReleaseNote, Error, string> => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      const response = await irnService.cancel(id);
      return response.data?.data || response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['irns'] });
      queryClient.invalidateQueries({ queryKey: ['irn', data.id] });
      if (data?.work_order_id) {
        queryClient.invalidateQueries({ queryKey: ['irn', 'work-order', data.work_order_id] });
        queryClient.invalidateQueries({ queryKey: ['work-orders', data.work_order_id] });
      }
      if (data?.delivery_note_id) {
        queryClient.invalidateQueries({ queryKey: ['irn', 'delivery-note', data.delivery_note_id] });
      }
      message.success('IRN cancelled');
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || 'Failed to cancel IRN');
    },
  });
};

export const useCheckCanPrepareDispatch = (workOrderId: string): UseQueryResult<{ canPrepareDispatch: boolean }, Error> => {
  return useQuery({
    queryKey: ['can-prepare-dispatch', workOrderId],
    queryFn: async () => {
      const response = await irnService.checkCanPrepareDispatch(workOrderId);
      return response.data?.data || response.data;
    },
    enabled: !!workOrderId,
  });
};

// =====================================================
// WORKSTATION, SHIFT & USER HOOKS
// =====================================================
export const useWorkstations = (activeOnly: boolean = true): UseQueryResult<Workstation[], Error> => {
  const company_id = getQcContextCompanyId();
  return useQuery({
    queryKey: ['workstations', 'qc-workflow', company_id, activeOnly],
    queryFn: async () => {
      const response = await (activeOnly ? workstationService.getActive() : workstationService.getAll());
      const raw: Workstation[] = response.data?.data || response.data || [];
      if (!Array.isArray(raw)) return [];
      if (activeOnly) return raw.filter((w) => w.is_active !== false);
      return raw;
    },
    enabled: !!company_id,
  });
};

export const useShifts = (activeOnly: boolean = true): UseQueryResult<Shift[], Error> => {
  const company_id = getQcContextCompanyId();
  return useQuery({
    queryKey: ['shifts', 'qc-workflow', company_id, activeOnly],
    queryFn: async () => {
      const response = await (activeOnly ? shiftService.getActive() : shiftService.getAll());
      const raw: Shift[] = response.data?.data || response.data || [];
      if (!Array.isArray(raw)) return [];
      if (activeOnly) return raw.filter((s) => s.is_active !== false);
      return raw;
    },
    enabled: !!company_id,
  });
};

export const useUsers = (filters?: { is_active?: boolean }): UseQueryResult<any[], Error> => {
  const companyId = useAuthStore((s) => s.user?.company_id);
  return useQuery({
    queryKey: ['users', companyId, filters],
    queryFn: async () => {
      const users = await userService.getAll(filters);
      return users || [];
    },
    enabled: !!companyId,
  });
};
